import type { ClientProcessDto, ClientProcessStepDto } from '@nsb/core';
import { dateWords } from '../days.ts';
import { clientEarly } from './tree/client.ts';

/**
 * Working on, under the funnel on both homes (design study 31; Jason, 2026-10-29: "a simplified 'currently working on',
 * that shows the nodes that are underway as a simple grid, with sort of a big arrow pointing right as 'working toward'
 * and then on the right the next major milestone"). Read from what the client's drawing reads (`ClientProcessDto`), so
 * NSB and the client see the same steps, each in their own words: the steps under way, then the ones ready to start
 * (q-home-grid B), and what they work toward, the next unfinished gate or milestone (L-34, Jason, 2026-10-29: "there
 * should always be something we are working toward, and it would be the next unfinished gate or milestone").
 */
export interface WorkingOn {
  /** The steps under way, by id, the one finishing first first. */
  under: string[];
  /** The steps ready to start, the work having reached them (the client's own among them), the one finishing first first. */
  ready: string[];
  toward: Toward | null;
}

/** The mark the work goes toward, and what is left of its phase. */
export interface Toward {
  id: string;
  name: string;
  /** The phase it closes, by name; null for one that closes none. */
  closes: string | null;
  /** The phase it is in, by name. */
  phase: string;
  /** Its day, only where someone planned one: the funnel's date for it (`plannedOn`), never one worked out from lengths. */
  on: string | null;
  /** Its phase's steps not done yet, and all of them, on this job. */
  left: number;
  of: number;
}

/** In the order they finish: the first first, one with no date after the rest, then the order given. */
function finishingFirst(steps: ClientProcessStepDto[]): string[] {
  return steps
    .map((s, n) => ({ s, n }))
    .sort((a, b) => (a.s.finish ?? '9999').localeCompare(b.s.finish ?? '9999') || a.n - b.n)
    .map(({ s }) => s.id);
}

export function workingOn(dto: ClientProcessDto): WorkingOn {
  const ids = new Set(dto.steps.map((s) => s.id));
  // The steps as the drawing draws them: not a step's own tasks, never the tree's start or end.
  const top = dto.steps.filter((s) => (s.parentId === null || !ids.has(s.parentId)) && !(dto.start && s.key === dto.start) && s.key !== 'project.archive');
  // And a task done in an earlier phase than its step, drawn in that phase (design study 31, slice 4: "shows on Working on
  // while it is under way"), in its own right: its step is not under way by it. The outermost, as the drawing has them.
  const early = clientEarly(dto);
  const toward = towardOf(dto, top);
  // The mark itself is never also a step under way or ready (a gate once the rest of its phase is done): it is the mark.
  const others = [...top, ...early].filter((s) => s.id !== toward?.id);
  const under = finishingFirst(others.filter((s) => s.state === 'now'));
  // Ready to start: not started, and the work has reached it (`reached`: nothing it waits on open, nothing holding it).
  const ready = finishingFirst(others.filter((s) => s.state !== 'done' && s.state !== 'now' && s.reached));
  return { under, ready, toward };
}

/**
 * The next unfinished gate or milestone: one no other unfinished gate or milestone comes before, however far back along
 * the job's waits. Where several are (work running side by side), the one in the earliest phase, then one the work has
 * reached, then the one finishing first, then the order given.
 */
function towardOf(dto: ClientProcessDto, top: ClientProcessStepDto[]): Toward | null {
  const ids = new Set(dto.steps.map((s) => s.id));
  const byId = new Map(dto.steps.map((s) => [s.id, s]));
  // A task's wait is its step's: the waits run between the steps as drawn.
  const topOf = (id: string): string => {
    let s = byId.get(id);
    for (let i = 0; s && s.parentId !== null && ids.has(s.parentId) && i < 100; i++) s = byId.get(s.parentId);
    return s?.id ?? id;
  };
  const before = new Map<string, Set<string>>();
  for (const l of dto.links) {
    const [step, needs] = [topOf(l.stepId), topOf(l.needsId)];
    if (step !== needs) before.set(step, new Set([...(before.get(step) ?? []), needs]));
  }
  const marks = top.filter((s) => (s.kind === 'gate' || s.kind === 'milestone') && s.state !== 'done');
  const unfinished = new Set(marks.map((s) => s.id));
  const behind = (id: string): boolean => {
    const seen = new Set<string>();
    const walk = (x: string): boolean => {
      for (const n of before.get(x) ?? []) {
        if (seen.has(n)) continue;
        seen.add(n);
        if (unfinished.has(n) || walk(n)) return true;
      }
      return false;
    };
    return walk(id);
  };
  const phases = (dto.phases ?? []).map((p) => p.key);
  const next = marks
    .map((s, n) => ({ s, n }))
    .filter(({ s }) => !behind(s.id))
    .sort((a, b) => phases.indexOf(a.s.phase) - phases.indexOf(b.s.phase) || Number(b.s.reached) - Number(a.s.reached) || (a.s.finish ?? '9999').localeCompare(b.s.finish ?? '9999') || a.n - b.n)[0]?.s;
  if (!next) return null;
  const phase = (dto.phases ?? []).find((p) => p.key === next.phase);
  const inPhase = top.filter((s) => s.phase === next.phase);
  const closes = next.key !== null && (dto.closers ?? []).includes(next.key);
  return {
    id: next.id,
    name: next.title,
    closes: closes ? (phase?.name ?? null) : null,
    phase: phase?.name ?? next.phase,
    on: next.plannedOn,
    left: inPhase.filter((s) => s.state !== 'done').length,
    of: inPhase.length,
  };
}

/** The words under the mark: what it closes (or the phase it is in), its day, and what is left of its phase. */
export function towardWords(t: Toward): string {
  const where = t.closes ? `Closes ${t.closes}.` : `In ${t.phase}.`;
  const when = t.on ? `About ${dateWords(t.on, 'monthYear')}.` : 'Not dated.';
  const left = `${t.left} of ${t.phase}’s ${t.of} ${t.of === 1 ? 'step' : 'steps'} on this job ${t.left === 1 ? 'is' : 'are'} not done yet.`;
  return `${where} ${when} ${left}`;
}
